import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { set, get, isEmpty, flatMap, isEqual, cloneDeep, isArray, pick, chain } from 'lodash';
import { setPreviousProductCostingData } from '../store/productCosting.actions';
import {
  averageCostingFieldpath,
  quantitiesFieldpath,
  testingFieldpath,
  landedInfoFieldPath,
  additionalFieldPath,
  detailedCostingFieldpath,
  additionalComponentFieldPath,
} from '../fieldPath';
import { useProductCostingState } from '../ProductCostingContext';
import { rebaseExchangeInfoForSupplier, resolveDcDefaultCurrValue } from '../exchange-rate.helpers';
import { clearRoyaltyFees } from '../landed-royalty.helpers';
import { getSavedLandedCombinationRows, restrictRowsToSavedCombinations } from '../landed-combination.helpers';
import type { LandedCombinationRow } from '../landed-combination.helpers';
import {
  calculateTotValues,
  createCostingDynamicGroupedArray,
  cloneData,
  checkArray,
  roundOff,
  roundingOff,
  convertedCurrValue,
  checkIsNanAndNumber,
  sumPricePerUnitTestingAdditionalSection,
} from '../../../../Components/helper';
import { getCostingTemplateDetailsById } from '../../../Library/CostingTemplateTSX/store/costingTemplate.actions';
import { toasterMsg } from '../../../../Components/Toaster/Toaster';
import useShowHideSectionHooks from './useShowHideSectionHooks';
import useDetailedCostingHooks from './useDetailedCostingHooks';
import type { AdditionalComponentState, AdditionalState, DetailedQuantitiesState, LandedInfoStateEntry, ProductCostingState, TestingState } from '../types';
import type { ExchangeRateMasterEntry } from '../exchange-rate.helpers';
import { mergeTemplateRequirements } from '../specification-requirements.helpers';
import type { CostingTemplateDetail } from '../store/productCosting.types';
import { automatedFreightPerUnit, automatedFreightTotal, freightAutomatedRowIssue, freightConversionRate, isFreightAutomatedActive, totalCostingQuantity } from '../automated-freight.helpers';
import { isSwitchedOffFreightColumn, resolveFreightContribution } from '../../../Dashboard/TodoList/CostingTsx/calc/landed';

/* Every shape below is deliberately open. The costing document is assembled at runtime by the
   template engine: a template's landed columns add arbitrary `<aliasName>` /
   `<aliasName>CalculatedValue` pairs to each row, and the calculation chain writes derived fields
   onto the same objects. Naming only the fields the code actually reads keeps the types useful
   without pretending the schema is closed. */

/** A `{ _id, name }` reference as embedded in colourway / size / length option arrays. */
interface NamedRefLike {
  _id?: string;
  name?: string;
  [key: string]: unknown;
}

/** One colourway/size/length combination row. */
interface ComboRow {
  colourway?: NamedRefLike | string | null;
  colourWay?: NamedRefLike | string | null;
  size?: string;
  length?: NamedRefLike | null;
  quantityPerSizCol?: unknown;
  pricePerUnit?: unknown;
  totalPrice?: unknown;
  multiQuantityRows?: MultiQuantityRow[] | undefined;
  defaultRowIndex?: number | undefined;
  quantityUnitOfMeasure?: NamedRefLike | string | null | undefined;
  isExcluded?: boolean | undefined;
  [key: string]: unknown;
}

/** One editable quantity/price line inside an average-costing combination. */
interface MultiQuantityRow {
  quantityPerSizCol?: unknown;
  pricePerUnit?: unknown;
  totalPrice?: unknown;
  [key: string]: unknown;
}

/** A parent group of combination rows (one per colourway, typically). */
interface ComboGroup {
  colourway?: NamedRefLike | null;
  expandData?: ComboRow[] | undefined;
  sizeRange?: unknown;
  lengthRange?: unknown;
  sumOfQuantityPerSizCol?: unknown;
  sumTotalPrice?: unknown;
  sumOfPricePerUnit?: unknown;
  [key: string]: unknown;
}

/** One landed-information row. Template columns add arbitrary alias keys on top of these. */
interface LandedRow {
  colourway?: NamedRefLike | string | null;
  colourWay?: NamedRefLike | string | null;
  size?: string;
  length?: NamedRefLike | null;
  landedInfo?: LandedInfoCell[];
  quantityPerSizCol?: unknown;
  pricePerUnitIncoConverted?: unknown;
  pricePerUnitLanded?: unknown;
  totalLanded?: unknown;
  sellingPriceGross?: unknown;
  sellingPriceNet?: unknown;
  estimatedRetailValue?: unknown;
  estimatedProfitPerUnit?: unknown;
  totalDefaultInfoAmount?: unknown;
  totalDefaultInfoPercent?: unknown;
  [key: string]: unknown;
}

/** One dynamic landed column value on a row. */
interface LandedInfoCell {
  _id?: string;
  type?: string;
  title?: string;
  tempValue?: unknown;
  calculatedValue?: unknown;
  colName?: unknown;
  colValue?: unknown;
  [key: string]: unknown;
}

/** A template column definition, keyed by `aliasName`. */
interface TemplateColumn {
  _id?: string;
  type?: string;
  aliasName?: string;
  [key: string]: unknown;
}

/** The costing template as this hook reads it. */
interface CostingTemplateLike {
  _id?: string;
  defaultColumns?: TemplateColumn[];
  bottomColumnArr?: TemplateColumn[];
  landedInfoRowArr?: LandedRow[];
  landedInfo?: LandedInfoCell[];
  additionalInfo?: ComboRow[];
  additionalComponentInfo?: ComboRow[];
  findExcahngeRateCurrency?: unknown;
  [key: string]: unknown;
}

/** Exchange values used instead of the ones held in state, when recalculating one DC. */
interface ExchangeOverride {
  primaryCurrencyValue?: unknown;
  secondaryCurrencyValue?: unknown;
  dcCurrency?: string | undefined;
}

/** `{ agentFee }` for a supplier that has been selected but not yet committed to state. */
interface SupplierOverride {
  agentFee?: unknown;
}

/** A BOM component row as the detailed-costing builder reads it. */
interface BomRow {
  _id?: string;
  pricePerUnit?: unknown;
  quantity?: unknown;
  quantityUnitOfMeasure?: unknown;
  colourWayRefsData?: ColourWayRefData[] | undefined;
  detailedCostingInfo?: ComboGroup[] | undefined;
  expandData?: ComboRow[] | undefined;
  [key: string]: unknown;
}

interface ColourWayRefData {
  colourWayId?: NamedRefLike;
  colourRefsDataAsPerSuppliers?: Array<{ tier1Supplier?: { _id?: string }; colourRef?: { name?: string } }>;
  [key: string]: unknown;
}

/** A testing library entry, and the testing row built from it. */
interface TestingRowLike {
  colourway?: NamedRefLike | null;
  colourRef?: NamedRefLike | string | null;
  testingCode?: NamedRefLike | null;
  totalPrice?: unknown;
  pricePerUnit?: unknown;
  isExcluded?: boolean;
  [key: string]: unknown;
}

/** The redux slices this hook reads. */
interface DetailedPageRootState {
  newProductCostingData: {
    bomCompoList?: BomRow[];
    productLengths?: NamedRefLike[];
    productExchnageRate?: { exchangeRateList?: ExchangeRateMasterEntry[]; defaultCurrencyValue?: unknown } | null;
    costingTemplateDetail?: CostingTemplateLike | null;
  };
  SupplierSuggestion: { TestingLibraries?: TestingLibraryEntry[] };
  BOMConfigDetails: { productDetails?: ProductDetailsLike };
  SystemMastersDetails: {
    licenseList?: LicenceLike[];
    likenessList?: LicenceLike[];
    subBrandList?: Array<{ _id?: string; currency?: string }>;
  };
  UserDetails: { userInfo?: { companyCurrency?: string } };
}

interface TestingLibraryEntry {
  _id?: string;
  colour?: unknown;
  description?: unknown;
  type?: unknown;
  bomComponent?: { referenceCode?: unknown };
  [key: string]: unknown;
}

interface ProductDetailsLike {
  sellingPrice?: unknown;
  licenseId?: string;
  likenessId?: string;
  subBrandId?: NamedRefLike | string;
  [key: string]: unknown;
}

/** A licence or likeness master entry carrying per-incoterm royalty percentages. */
interface LicenceLike {
  _id?: string;
  incoterms?: Array<{ incotermsId?: { _id?: string }; value?: unknown }>;
  [key: string]: unknown;
}

/** Options accepted by `recalculateLandedInfoStates`. */
export interface RecalculateLandedInfoOptions {
  incotermVal?: NamedRefLike | null;
  dcStates?: LandedInfoStateEntry[];
  supplierOverride?: SupplierOverride;
  reconvertFromGrandTotal?: boolean;
}

/**
 * The calculation engine behind the Product Costing detail page: average costing, quantities,
 * detailed costing, testing, setup cost, additional components and the whole landed-information
 * chain, plus the save-payload shaping.
 *
 * Ported 1:1 from NewProductCosting/Hooks/useDetailedPageHook.js.
 */
export default function useDetailedPageHook() {
  const dispatch = useDispatch();
  const {
    newProductCostingData: { bomCompoList, productLengths, productExchnageRate, costingTemplateDetail },
    SupplierSuggestion: { TestingLibraries },
    BOMConfigDetails: { productDetails },
    SystemMastersDetails: { licenseList, likenessList, subBrandList },
    UserDetails: { userInfo },
  } = useSelector((reduxState: DetailedPageRootState) => reduxState);
  const {
    productCostingstate,
    setProductCostingState,
    initialSetting,
    internalState,
    grandTotalState,
    initialOptionsState,
    testingState,
    setTestingState,
    colSizeLengthToggleState,
    setAdditionalState,
    setLandedInfoState,
    landedInfoStates,
    setLandedInfoStates,
    detailedQuanitiesState,
    setDetailedQuanitiesState,
    detailedCostingState,
    setAdditionalComponentState,
    setSpecificationRequirementState,
    additionalState,
    automatedFreight,
  } = useProductCostingState();
  const { colourWayToggle, sizeLengthToggle } = colSizeLengthToggleState;

  const {
    additional: { additionalInfo },
  } = additionalState;

  const {
    testing: { testingInfo },
  } = testingState;

  const {
    productInfo: { supplierId, incoterms },
    productTemplate,
    isBreakdown,
    averageCosting: { averageCostingActive },
  } = productCostingstate;
  const { sizeOptionsArr, colourwayOptionsArr } = initialOptionsState;
  const { grandTotal } = grandTotalState;
  const { hideLengthSysMaster } = useShowHideSectionHooks();
  const { calculateDetailCosting } = useDetailedCostingHooks();

  /* `supplierId` is a populated supplier object on this state, not an id string. */
  const supplierRef = supplierId as unknown as { _id?: string; companies?: { agentFee?: unknown; currency?: string } } | null;

  const calculateAvrageCosting = async (copyState: ProductCostingState, colourwayId?: string): Promise<void> => {
    const totLength: unknown[] = [];
    const totSize: unknown[] = [];
    const totColourways: unknown[] = [];
    let totSize1: unknown[] = [];
    const updatedArray = (copyState?.averageCosting?.averageCostingInfo as ComboGroup[] | undefined)?.map((parentData) => {
      if (internalState?.hideColourCol) {
        totSize1 = [];
      }
      const expandArray = parentData?.expandData;
      let sumOfTotalPrice: string | number = '0';
      let sumOfQuantityPerSizCol: string | number = '0';
      let sumPricePerUnit: string | number = '0';
      let totWeightagePricePerUnit: string | number = '0';
      let totalQuantity: string | number = '0';
      parentData.expandData = expandArray?.map((element, inde) => {
        const mqRows = element?.multiQuantityRows || [];
        const defaultIdx = element?.defaultRowIndex ?? 0;
        element.multiQuantityRows = mqRows.map((row) => {
          row.totalPrice = roundingOff(Number(row?.pricePerUnit || 0) * Number(row?.quantityPerSizCol || 0), 2);
          return row;
        });
        const selectedRow = element.multiQuantityRows[defaultIdx] || element.multiQuantityRows[0] || {};
        element.quantityPerSizCol = selectedRow?.quantityPerSizCol || '0';
        element.pricePerUnit = selectedRow?.pricePerUnit || '0';
        const { quantityPerSizCol } = element;
        const { pricePerUnit } = element;
        const totalPrice = roundingOff(Number(pricePerUnit) * Number(quantityPerSizCol), 2);
        if (!isBreakdown) {
          totLength.push((element?.length as NamedRefLike | null)?.name);
          totSize.push(element?.size);
          totSize1.push(element?.size);
          sumOfTotalPrice = +sumOfTotalPrice + +totalPrice;
          sumOfQuantityPerSizCol = +sumOfQuantityPerSizCol + Number(quantityPerSizCol);
          sumPricePerUnit = +sumPricePerUnit + Number(pricePerUnit);
          element.totalPrice = totalPrice;
          const weightagePricePerUnit = Number(quantityPerSizCol) * Number(pricePerUnit);
          totWeightagePricePerUnit = checkIsNanAndNumber(weightagePricePerUnit) + checkIsNanAndNumber(totWeightagePricePerUnit);
          totalQuantity = checkIsNanAndNumber(totalQuantity) + checkIsNanAndNumber(quantityPerSizCol);
        } else if (isBreakdown && inde === 0) {
          sumOfTotalPrice = +sumOfTotalPrice + +totalPrice;
          sumOfQuantityPerSizCol = +sumOfQuantityPerSizCol + Number(quantityPerSizCol);
          sumPricePerUnit = +sumPricePerUnit + Number(pricePerUnit);
          element.totalPrice = totalPrice;
        }
        totColourways.push(parentData?.colourway?.name);
        return element;
      });
      const unique = (arr: unknown[]): unknown[] => [...new Set(arr)];
      if (!internalState?.hideColourCol) {
        parentData.sizeRange = unique(totSize).map((vl) => ({ name: vl, label: vl }));
        parentData.lengthRange = unique(totLength).map((vl) => ({ name: vl, label: vl }));
      } else {
        parentData.sizeRange = unique(totSize1).map((vl) => ({ name: vl, label: vl }));
        parentData.lengthRange = unique(totLength).map((vl) => ({ name: vl, label: vl }));
      }
      parentData.sumOfQuantityPerSizCol = sumOfQuantityPerSizCol;
      parentData.sumTotalPrice = sumOfTotalPrice;
      parentData.sumOfPricePerUnit = sumPricePerUnit;
      parentData.totWeightagePricePerUnit = Number(totWeightagePricePerUnit) / Number(totalQuantity);
      if (colourwayId === parentData?.colourway?._id) {
        handleColourbasedTestingModuleChange(colourwayId, Number(sumOfQuantityPerSizCol));
      }

      return parentData;
    });
    const data = await calculateTotValues(updatedArray, [
      {
        key: 'sumOfQuantityPerSizCol',
        updateKey: 'overAllSumOfQuantityPerSizCol',
      },
      { key: 'sumTotalPrice', updateKey: 'overAllSumOfTotalPrice' },
      { key: 'sumOfPricePerUnit', updateKey: 'overAllSumOfPricePerUnit' },
    ]);
    set(copyState, averageCostingFieldpath().EXPAND_OVERALL_SUM_TOTAL_PRICE, data?.overAllSumOfTotalPrice);
    set(copyState, averageCostingFieldpath().EXPAND_OVERALL_SUM_QUANTITY_PER_COL_SIZ, data?.overAllSumOfQuantityPerSizCol);
    set(copyState, averageCostingFieldpath().EXPAND_OVERALL_SUM_PRICE_PER_UNIT, data?.overAllSumOfPricePerUnit);
    set(copyState, averageCostingFieldpath().TOTAL_AVERAGE_COLOURWAYS, [...new Set(totColourways)]);
    set(copyState, averageCostingFieldpath().TOTAL_AVERAGE_LENGTH, [...new Set(totLength)]);
    set(copyState, averageCostingFieldpath().TOTAL_AVERAGE_SIZES, [...new Set(totSize)]);
    set(copyState, averageCostingFieldpath().AVERAGE_COSTING_INFO, updatedArray);
    set(copyState, averageCostingFieldpath().LOADER, false);
    if (!colourwayId) {
      handleTestingModuleChange(data?.overAllSumOfQuantityPerSizCol);
    }
    setProductCostingState((prevState) => ({ ...prevState, ...copyState }));
  };

  const calculateQuantities = async (copyState: DetailedQuantitiesState, colourwayId?: string): Promise<void> => {
    const copyStates = copyState;
    let overAllSumOfQuantityPerSizCol: string | number = '0';
    const totLength: unknown[] = [];
    const totSize: unknown[] = [];
    const totColourways: unknown[] = [];
    const updatedArray = (copyStates?.quantities?.quantitiesInfo as ComboGroup[] | undefined)?.map((parentData) => {
      const expandArray = parentData?.expandData;
      let sumOfQuantityPerSizCol: string | number = '0';
      parentData.expandData = expandArray?.map((data, ind) => {
        const { quantityPerSizCol } = data;
        if (!isBreakdown) {
          sumOfQuantityPerSizCol = +sumOfQuantityPerSizCol + Number(quantityPerSizCol);
          if (!isEmpty(data?.length)) {
            totLength.push((data?.length as NamedRefLike | null)?.name);
          }
          totSize.push(data?.size);
        } else if (isBreakdown && ind === 0) {
          sumOfQuantityPerSizCol = +sumOfQuantityPerSizCol + Number(quantityPerSizCol);
        }
        totColourways.push(parentData?.colourway?.name);
        return data;
      });
      if (!internalState?.hideColourCol) {
        parentData.sizeRange = totSize
          .filter((item, index) => totSize.indexOf(item) === index)
          .map((vl) => {
            return { name: vl, label: vl };
          });
        parentData.lengthRange = totLength
          .filter((item, index) => totLength.indexOf(item) === index)
          .map((vl) => {
            return { name: vl, label: vl };
          });
      }
      parentData.sumOfQuantityPerSizCol = sumOfQuantityPerSizCol;
      overAllSumOfQuantityPerSizCol = +overAllSumOfQuantityPerSizCol + +sumOfQuantityPerSizCol;
      if (colourwayId === parentData?.colourway?._id) {
        handleColourbasedTestingModuleChange(colourwayId, Number(sumOfQuantityPerSizCol));
      }
      return parentData;
    });
    set(copyStates, quantitiesFieldpath().QUANTITES_INFO, updatedArray);
    set(copyState, quantitiesFieldpath().TOTAL_AVERAGE_COLOURWAYS, [...new Set(totColourways)]);
    set(copyState, quantitiesFieldpath().TOTAL_AVERAGE_LENGTH, [...new Set(totLength)]);
    set(copyState, quantitiesFieldpath().TOTAL_AVERAGE_SIZES, [...new Set(totSize)]);
    set(copyStates, 'quantities.overAllSumOfQuantityPerSizCol', overAllSumOfQuantityPerSizCol);
    if (!colourwayId) {
      handleTestingModuleChange(copyStates?.quantities?.overAllSumOfQuantityPerSizCol);
    }
    setDetailedQuanitiesState((state) => ({ ...state, ...copyStates }));
  };

  /**
   * Recomputes the per-unit testing price for rows of a single colourway when its
   * average/quantities total changes. Reads the latest testing state via the functional
   * updater (not a captured closure) so concurrently-typed testing values are preserved
   * instead of being overwritten by a stale snapshot.
   */
  const handleColourbasedTestingModuleChange = (colourId: string | undefined, sumOfQuantityPerSizCol: number): void => {
    setTestingState((prevState) => {
      const copyState: TestingState = cloneDeep(prevState);
      const testingData = get(copyState, testingFieldpath().TESTING_INFO_DATA, []) as TestingRowLike[];
      if (!checkArray(testingData)) return prevState;
      const updatedTestingData = testingData.map((element) => {
        if (element?.colourway?._id === colourId) {
          element.pricePerUnit = Number(element?.totalPrice) / sumOfQuantityPerSizCol;
        }
        return element;
      });
      set(copyState, 'testing.testingInfo', updatedTestingData);
      return copyState;
    });
  };

  /**
   * Recomputes the per-unit testing price for every testing row when the overall
   * average/quantities total changes. Reads the latest testing state via the functional
   * updater so concurrently-typed testing values are preserved.
   */
  const handleTestingModuleChange = (quantitiesOverAllSumOfQuantityPerSizCol: unknown): void => {
    setTestingState((prevState) => {
      const copyState: TestingState = cloneDeep(prevState);
      const testingData = get(copyState, testingFieldpath().TESTING_INFO_DATA, []) as TestingRowLike[];
      if (!checkArray(testingData)) return prevState;
      const updatedTestingData = testingData.map((element) => {
        if (Number(quantitiesOverAllSumOfQuantityPerSizCol) > 0) {
          element.pricePerUnit = roundOff(Number(element?.totalPrice) / Number(quantitiesOverAllSumOfQuantityPerSizCol));
        } else {
          element.pricePerUnit = '0';
        }
        return element;
      });
      set(copyState, 'testing.testingInfo', updatedTestingData);
      return copyState;
    });
  };

  useEffect(() => {
    return () => {
      dispatch(setPreviousProductCostingData(null as never) as never);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const manipualteDetailedCostingDataOnChange = async (state: ProductCostingState, suppid?: string): Promise<ProductCostingState> => {
    const copyState = state;
    set(copyState, 'averageCosting', initialSetting?.averageCosting);
    const cloneBomList: BomRow[] = cloneData(bomCompoList);
    const copyBomList: BomRow[] = [];
    for (const element of cloneBomList) {
      element.colourWayRefsData = element?.colourWayRefsData?.filter((vl) => {
        return vl?.colourRefsDataAsPerSuppliers?.some((colour) => colour?.tier1Supplier?._id === suppid);
      });
      copyBomList.push(element);
    }

    const updatedArray: BomRow[] = [];
    const totSizes = sizeOptionsArr?.map((data) => data?.name);
    const totlegLengths = productLengths?.map((data) => data?.name);

    for (const element of copyBomList) {
      const setKeys = [
        { key: 'quantityPerSizCol', value: '' },
        { key: 'quantityUom', value: element.quantityUnitOfMeasure },
        { key: 'totalQuantityUom', value: null },
        { key: 'pricePerUnit', value: '' },
      ];
      if (Array.isArray(element.colourWayRefsData) && element.colourWayRefsData.length > 0) {
        const colourwayIds: Array<{ colourWay?: NamedRefLike | undefined; colourRef?: string | undefined }> = [];
        for (const childElement of element.colourWayRefsData) {
          const elementObj: { colourWay?: NamedRefLike | undefined; colourRef?: string | undefined } = {
            colourWay: childElement.colourWayId,
          };
          const colourRef: Array<string | undefined> = [];
          for (const colourSupp of childElement?.colourRefsDataAsPerSuppliers || []) {
            colourRef.push(colourSupp?.colourRef?.name);
          }
          elementObj.colourRef = (colourRef || []).join(', ');
          colourwayIds.push(elementObj);
        }
        if (colourwayIds && colourwayIds?.length > 0) {
          const totColourways = colourwayIds?.map((data) => data?.colourWay);
          const parentKeys = [
            {
              key: 'totLength',
              value: totlegLengths,
            },
            {
              key: 'totSize',
              value: totSizes,
            },
            {
              key: 'totColourways',
              value: totColourways,
            },
          ];
          const manipullatedAverageData = (await createCostingDynamicGroupedArray(colourwayIds, sizeOptionsArr, productLengths, setKeys, parentKeys)) as ComboGroup[];

          element.detailedCostingInfo = manipullatedAverageData;
          element.totLength = [...new Set((productLengths || []).map((data) => data?.name))];
          element.totSize = [...new Set((sizeOptionsArr || []).map((data) => data))];
          element.totColourways = [...new Set(colourwayIds.map((data) => (data as unknown as NamedRefLike)?.name))];
        }
      } else {
        const totColourways = colourwayOptionsArr.map((data) => data?.name);

        const parentKeys = [
          {
            key: 'totLength',
            value: totlegLengths,
          },
          {
            key: 'totSize',
            value: totSizes,
          },
          {
            key: 'totColourways',
            value: totColourways,
          },
        ];

        const manipullatedAverageData = (await createCostingDynamicGroupedArray(colourwayOptionsArr, sizeOptionsArr, productLengths, setKeys, parentKeys)) as ComboGroup[];
        element.detailedCostingInfo = manipullatedAverageData;

        element.totLength = [...new Set((productLengths || []).map((data) => data?.name))];
        element.totSize = [...new Set((sizeOptionsArr || []).map((data) => data))];
        element.totColourways = [...new Set(colourwayOptionsArr.map((data) => data?.name))];
      }

      updatedArray.push(element);
    }
    set(copyState, 'detailedCosting.bomTypeArr', updatedArray);
    setProductCostingState((prevState) => ({ ...prevState, ...copyState }));
    return copyState;
  };

  /**
   * Reshapes one module's rows into the flat id-only form the save endpoint expects.
   *
   * `undefined` vs `null` is load-bearing here — do NOT add `?? null` to these `_id` reads.
   * `JSON.stringify` DROPS a key whose value is `undefined` but PERSISTS one that is `null`, so a
   * defensive fallback changes what reaches the database: the original omits the key entirely,
   * while `?? null` writes an explicit `length: null` / `colourway: null` onto the stored document.
   * Only mirror the `|| null` fallbacks that the JavaScript original actually has (the `expandData`
   * rows) — every other `_id` read must be allowed to stay `undefined`.
   */
  const manipualtedProductCostingSaveData = async (data: unknown, type: string): Promise<unknown[]> => {
    let array: unknown[] = [];
    const updatedData: ComboGroup[] = cloneData(data || []);
    const source = data as { sizeRange?: unknown[]; lengthRange?: unknown[] };
    switch (type) {
      case 'averageCosting':
      case 'quantities':
        array = updatedData?.map((element) => {
          element.colourway = element?.colourway?._id as never;
          element.expandData = element?.expandData?.map((expElement) => {
            expElement.colourway = (expElement?.colourway as NamedRefLike)?._id as never;
            expElement.length = ((expElement?.length as NamedRefLike)?._id || null) as never;
            expElement.multiQuantityRows = expElement?.multiQuantityRows || [];
            expElement.defaultRowIndex = expElement?.defaultRowIndex ?? 0;
            return expElement;
          });
          element.sizeRange = Array.isArray(source.sizeRange)
            ? source?.sizeRange?.filter((item) => (item as NamedRefLike)?.name != null || (item as { label?: unknown })?.label != null)?.map((vl) => (vl as NamedRefLike)?.name)
            : [];
          element.lengthRange = Array.isArray(source.lengthRange)
            ? source?.lengthRange?.filter((item) => Object.keys((item as object) || {}).length > 0)?.map((vl) => (vl as NamedRefLike)?.name)
            : [];
          return element;
        });
        break;
      case 'landedInfo':
        array = (updatedData as unknown as LandedRow[])?.map((element) => {
          element.colourway = ((element?.colourWay as NamedRefLike)?._id || (element?.colourway as NamedRefLike)?._id) as never;
          element.colourWay = ((element?.colourWay as NamedRefLike)?._id || (element?.colourway as NamedRefLike)?._id) as never;
          element.length = (element?.length as NamedRefLike)?._id as never;
          return element;
        });
        break;
      case 'detailedCosting':
        array = (updatedData as unknown as BomRow[])?.map((element) => {
          return {
            bomComponentId: pick(element, [
              'bomDescription',
              'bomSubType',
              'bomType',
              'libId',
              'quantityUnitOfMeasure',
              'quantity',
              'pricePerUnit',
              '_id',
              'referenceCode',
              'pricePerUnitCurrency',
            ]),
            totLength: element?.totLength,
            totSize: element?.totSize,
            totColourways: element?.totColourways,
            totColourRefs: element?.totColourRefs,
            overAllQuantity: element?.overAllQuantity,
            overAllTotalPrice: element?.overAllTotalPrice,
            expandData: element?.expandData?.map((expElement) => {
              expElement.colourway = ((expElement?.colourway as NamedRefLike)?._id || null) as never;
              expElement.length = ((expElement?.length as NamedRefLike)?._id || null) as never;
              expElement.quantityUnitOfMeasure = ((expElement?.quantityUnitOfMeasure as NamedRefLike)?._id || null) as never;
              return expElement;
            }),
          };
        });
        break;
      case 'testing':
        array = (updatedData as unknown as TestingRowLike[])?.map((element) => {
          return {
            colourway: element?.colourway?._id,
            testingCode: pick(element?.testingCode, ['bomComponent', 'code', 'additionalInformation', 'description', 'minPassRate', 'type', '_id', 'colour']),
            colourRef: (element?.colourRef as NamedRefLike)?._id || (element?.colourway as { colourRef?: unknown })?.colourRef,
            totalPrice: element?.totalPrice,
            pricePerUnit: element?.pricePerUnit,
            isChangedBySupplierTotalPrice: element?.isChangedBySupplierTotalPrice,
            isChangedBySupplierPricePerUnit: element?.isChangedBySupplierPricePerUnit,
            isExcluded: element?.isExcluded,
          };
        });
        break;
      case 'additional':
        array = (updatedData as unknown as ComboRow[])?.map((element) => {
          element.colourway = (element?.colourway as NamedRefLike)?._id as never;
          element.quantityUom = (element?.quantityUom as NamedRefLike)?._id as never;
          return element;
        });
        break;
      case 'grandTotal':
        array = updatedData?.map((element) => {
          element.colourway = element?.colourway?._id as never;
          element.length = (element?.length as NamedRefLike)?._id as never;
          element.expandData = (element?.expandData as ComboRow[]).map((expElement) => {
            expElement.colourway = ((expElement?.colourway as NamedRefLike)?._id || null) as never;
            expElement.length = ((expElement?.length as NamedRefLike)?._id || null) as never;
            return expElement;
          });
          element.sizeRange = Array.isArray(source.sizeRange)
            ? source?.sizeRange?.filter((item) => (item as NamedRefLike)?.name != null || (item as { label?: unknown })?.label != null)?.map((vl) => (vl as NamedRefLike)?.name)
            : [];
          element.lengthRange = Array.isArray(source.lengthRange)
            ? source?.lengthRange?.filter((item) => Object.keys((item as object) || {}).length > 0)?.map((vl) => (vl as NamedRefLike)?.name)
            : [];
          return element;
        });
        break;
      default:
        break;
    }
    return array;
  };

  /**
   * Rebuilds one distribution centre's Landed Information from a costing template response.
   *
   * The template endpoint derives its rows from the development order as it stands right now, so on a
   * saved costing they would introduce every colourway, size and length added to the product since.
   * `savedCombinationRows` narrows them back to the set the costing was saved with; callers that are
   * legitimately building a new combination set — creating a costing, or changing the colourway and
   * size toggles — omit it and get the full template response.
   *
   * @param res the costing template details response.
   * @param copyState the distribution centre's landed state.
   * @param gTotal grand total rows to cost against; falls back to the grand total held in state.
   * @param incotermVal incoterm to calculate with; falls back to the one held in state.
   * @param dcIndex index into `landedInfoStates`; omitted for the legacy single-centre state.
   * @param savedCombinationRows the landed rows the costing was saved with, when they must be preserved.
   */
  const handleLandedInfoModule = async (
    res: CostingTemplateLike,
    copyState: LandedInfoStateEntry,
    gTotal?: ComboGroup[],
    incotermVal?: NamedRefLike | null,
    dcIndex?: number,
    savedCombinationRows?: LandedCombinationRow[],
  ): Promise<void> => {
    const incotermState = incotermVal || incoterms;
    const cloneCopyState: LandedInfoStateEntry = cloneDeep(copyState);
    const updatedArray: LandedRow[] = [];
    const templateRowArr = restrictRowsToSavedCombinations(
      res?.landedInfoRowArr as LandedCombinationRow[] | undefined,
      savedCombinationRows,
    ) as unknown as CostingTemplateLike['landedInfoRowArr'];
    if (Array.isArray(templateRowArr) && templateRowArr.length > 0) {
      for (const land of templateRowArr) {
        land.landedCost = '';
        land.estimatedRetailValue = productDetails?.sellingPrice || '';
        land.sellingPriceGross = productDetails?.sellingPrice || '';
        if (land?.landedInfo?.length) {
          land.landedInfo = land?.landedInfo?.map((ele) => {
            return {
              ...ele,
              colName: ele?.title,
              colValue: ele?.tempValue,
            };
          });
        }
        updatedArray.push(land);
      }
    }

    const dcExchangeInfo = cloneCopyState?.landedExchangeRateInformation || {};
    const dcExchangedRate = dcExchangeInfo?.manualAdded ? dcExchangeInfo?.exchangedRate : undefined;
    const dcCompanyCurrencyValue = dcExchangeInfo?.companyCurrencyValue;
    const updatedLandedInfo = await grandTotalLandedInfoCalculation(gTotal || (grandTotal as ComboGroup[]), updatedArray, dcExchangedRate, dcCompanyCurrencyValue);
    const updateArrays = await calculateProfit(updatedLandedInfo, res, incotermState as NamedRefLike, {
      primaryCurrencyValue: dcExchangedRate || internalState?.primaryCurrValue,
      secondaryCurrencyValue: dcCompanyCurrencyValue,
      dcCurrency: dcExchangeInfo?.companyCurrency,
    });
    set(cloneCopyState, landedInfoFieldPath().LANDED_INFO_DATA, updateArrays);
    set(cloneCopyState, landedInfoFieldPath().LANDEDINFO_CURRENCY_ARR, res?.findExcahngeRateCurrency);

    if (typeof dcIndex === 'number' && dcIndex >= 0) {
      setLandedInfoStates((prev) => {
        const copy = [...prev];
        copy[dcIndex] = { ...(copy[dcIndex] as LandedInfoStateEntry), ...cloneCopyState };
        return copy;
      });
    } else {
      setLandedInfoState((prevState) => ({ ...prevState, ...cloneCopyState }));
    }
  };

  /**
   * Resolves one distribution centre's exchange-rate override for the calculation chain.
   * Mirrors getDcExchangeOverride in LandedInformationTable — the manual rate when that DC's toggle
   * is on, otherwise the master rate for the DC's own currency against the supplier currency — so a
   * recalculation driven from here and one driven by an in-grid edit always agree.
   */
  const buildDcExchangeOverride = (dcState: LandedInfoStateEntry): ExchangeOverride => {
    const exchangeInfo = dcState?.landedExchangeRateInformation || {};
    const dcCurrency = dcState?.distributionCentre?.currency || userInfo?.companyCurrency;
    const supplierCurrency = exchangeInfo?.supplierCurrency || productCostingstate?.productInfo?.currency;
    const dcDefaultRate = resolveDcDefaultCurrValue(
      productExchnageRate?.exchangeRateList,
      dcCurrency,
      supplierCurrency ?? undefined,
      internalState?.primaryCurrValue,
    );
    return {
      primaryCurrencyValue: exchangeInfo?.manualAdded ? exchangeInfo?.exchangedRate ?? 1 : dcDefaultRate,
      secondaryCurrencyValue: exchangeInfo?.companyCurrencyValue,
      dcCurrency: exchangeInfo?.companyCurrency || dcCurrency,
    };
  };

  /**
   * Recalculates the Landed Information rows of every distribution centre in place.
   *
   * The costing template is deliberately never refetched, so every value the user entered
   * (landedInfo tempValues, VAT, Customer Discount, Freight, Duty, Selling Price Gross, Estimated
   * Retail Value) survives and only the derived columns move. Distribution centres beyond the first
   * are not mounted in the collapsed view, so they can only be reached from here — never from an
   * effect inside LandedInformationTable.
   */
  const recalculateLandedInfoStates = async ({
    incotermVal,
    dcStates,
    supplierOverride,
    reconvertFromGrandTotal,
  }: RecalculateLandedInfoOptions = {}): Promise<LandedInfoStateEntry[] | null> => {
    const states = dcStates || landedInfoStates;
    const template = costingTemplateDetail || (internalState?.currSelectedTemplateDetails as unknown as CostingTemplateLike | null);
    if (!isArray(states) || states.length === 0 || !template) return null;

    let hasRecalculated = false;
    const updatedStates: LandedInfoStateEntry[] = [];
    for (const dcState of states) {
      const rows = get(dcState, landedInfoFieldPath().LANDED_INFO_DATA, []) as LandedRow[];
      if (!isArray(rows) || rows.length === 0) {
        updatedStates.push(dcState);
        continue;
      }
      const exchangeOverride = buildDcExchangeOverride(dcState);
      let workingRows: LandedRow[] = cloneDeep(rows);
      if (reconvertFromGrandTotal) {
        workingRows = (await grandTotalLandedInfoCalculation(
          grandTotal as ComboGroup[],
          workingRows,
          exchangeOverride.primaryCurrencyValue,
          exchangeOverride.secondaryCurrencyValue,
        )) as LandedRow[];
      }
      if (incotermVal) workingRows = clearRoyaltyFees(workingRows);
      const updatedRows = await calculateProfit(workingRows, template, (incotermVal || incoterms) as NamedRefLike, exchangeOverride, supplierOverride);
      const copyState: LandedInfoStateEntry = cloneDeep(dcState);
      set(copyState, landedInfoFieldPath().LANDED_INFO_DATA, updatedRows);
      updatedStates.push(copyState);
      hasRecalculated = true;
    }
    if (!hasRecalculated) return null;
    setLandedInfoStates(updatedStates);
    return updatedStates;
  };

  /**
   * Recalculates the Landed Information after an incoterm change.
   * Only the Licence and Likeness Royalty Fee columns depend on the incoterm, so the rows already on
   * screen are reused and just the profit chain is re-run — replacing the template rebuild that
   * previously discarded every entered value.
   */
  const recalculateLandedInfoForIncoterm = async (incotermVal: NamedRefLike | null): Promise<LandedInfoStateEntry[] | null> =>
    recalculateLandedInfoStates({ incotermVal });

  /**
   * Rebases every distribution centre onto a newly selected supplier and recalculates.
   *
   * The supplier currency is the one shown to the right of the Landed Information exchange-rate
   * field, so it is restamped on each DC and the rate re-seeded from the season master for that DC's
   * own currency. Any manual override is cleared, because a rate typed against the previous
   * supplier's currency no longer means anything. The new agent fee is passed explicitly since the
   * supplier has not yet been committed to state when this runs.
   */
  const applySupplierChangeToLandedInfo = async (nextSupplier: { companies?: { currency?: string; agentFee?: unknown } } | null): Promise<void> => {
    const supplierCurrency = nextSupplier?.companies?.currency;
    const agentFee = nextSupplier?.companies?.agentFee;
    const rebasedStates: LandedInfoStateEntry[] = (landedInfoStates || []).map((dcState) => ({
      ...dcState,
      /* `RebaseExchangeInfoInput` declares plain optional properties, and this call site genuinely
         has `string | undefined` values. The cast keeps exchange-rate.helpers.ts a byte-identical
         copy of the original rather than widening its published contract. */
      landedExchangeRateInformation: rebaseExchangeInfoForSupplier(dcState?.landedExchangeRateInformation, {
        dcCurrency: dcState?.distributionCentre?.currency || userInfo?.companyCurrency,
        supplierCurrency,
        exchangeRateList: productExchnageRate?.exchangeRateList,
      } as Parameters<typeof rebaseExchangeInfoForSupplier>[1]),
    }));
    if (rebasedStates.length === 0) return;
    const recalculated = await recalculateLandedInfoStates({
      dcStates: rebasedStates,
      supplierOverride: { agentFee },
      reconvertFromGrandTotal: true,
    });
    if (!recalculated) setLandedInfoStates(rebasedStates);
  };

  /**
   * Rebuilds the Setup Cost (additional) module from the costing template.
   * Preserves the user-entered values for any template row whose item name (title)
   * already exists in the current state, so switching costing type does not wipe data
   * that the template still references.
   */
  const handleAdditonalModule = async (
    res: CostingTemplateLike,
    copyState: AdditionalState,
    controlSetState?: boolean,
    _controlDetailedCosting?: boolean,
  ): Promise<AdditionalState | undefined> => {
    const normaliseTitle = (title: unknown): unknown => (typeof title === 'string' ? title.trim().toLowerCase() : title);
    const existingRows = (get(copyState, additionalFieldPath().SELECTED_TEMP_ADDTIONAL_DATA) || []) as ComboRow[];
    const existingByTitle = new Map<unknown, ComboRow[]>();
    existingRows.forEach((row) => {
      const key = normaliseTitle(row?.title);
      if (key === undefined || key === null || key === '') return;
      if (!existingByTitle.has(key)) existingByTitle.set(key, []);
      existingByTitle.get(key)?.push(row);
    });
    const takeExistingRow = (title: unknown): ComboRow | null => {
      const bucket = existingByTitle.get(normaliseTitle(title));
      return bucket && bucket.length ? (bucket.shift() as ComboRow) : null;
    };
    let hasPreservedRow = false;

    if (Array.isArray(res?.additionalInfo) && res?.additionalInfo.length > 0) {
      let response = res?.additionalInfo;
      response = response?.map((data) => {
        const preserved = takeExistingRow(data?.title);
        if (preserved) {
          hasPreservedRow = true;
          return {
            ...data,
            colourway: preserved.colourway ?? null,
            quantityUom: preserved.quantityUom ?? null,
            totalQuantityUom: preserved.totalQuantityUom ?? null,
            quantityPerUnit: preserved.quantityPerUnit ?? '',
            pricePerUnit: preserved.pricePerUnit ?? '',
            totalQuantity: preserved.totalQuantity ?? '',
            pricePerUoM: preserved.pricePerUoM ?? '',
            quantityPerColSizLen: preserved.quantityPerColSizLen ?? '',
            totalPrice: preserved.totalPrice ?? '',
            isExcluded: preserved.isExcluded ?? false,
            isPricePerUnitDisabled: preserved.isPricePerUnitDisabled ?? false,
            isTotalPriceDisabled: preserved.isTotalPriceDisabled ?? false,
          };
        }
        return {
          ...data,
          colourway: null,
          quantityUom: null,
          totalQuantityUom: null,
          quantityPerUnit: '',
          pricePerUnit: '',
          totalQuantity: '',
          pricePerUoM: '',
          quantityPerColSizLen: '',
          totalPrice: '',
          isPricePerUnitDisabled: false,
          isTotalPriceDisabled: false,
        };
      });
      set(copyState, additionalFieldPath().SELECTED_TEMP_ADDTIONAL_DATA, response);
      if (hasPreservedRow) {
        const nonExcluded = response.filter((item) => !item.isExcluded);
        const totals = await calculateTotValues(nonExcluded, [{ key: 'totalPrice', updateKey: 'sumOfTotPrice' }]);
        set(copyState, additionalFieldPath().SUM_TOTAL_PRICE, totals.sumOfTotPrice);
        if (controlSetState) {
          return copyState;
        }
        setAdditionalState((prevState) => ({ ...prevState, ...copyState }));
        return undefined;
      }
    } else {
      set(copyState, 'additional', {
        additionalInfo: [
          {
            colourway: null,
            quantityUom: null,
            totalQuantityUom: null,
            quantityPerUnit: '',
            pricePerUnit: '',
            title: '',
            totalQuantity: '',
            pricePerUoM: '',
            quantityPerColSizLen: '',
            totalPrice: '',
            isPricePerUnitDisabled: false,
            isTotalPriceDisabled: false,
          },
        ],
      });
    }
    set(copyState, additionalFieldPath().SUM_TOTAL_PRICE, '0');
    if (controlSetState) {
      return copyState;
    }
    setAdditionalState((prevState) => ({ ...prevState, ...copyState }));
    return undefined;
  };

  const handleAverageCosting = async (iscolourWayToggle: boolean, isSizeLengthToggle: boolean): Promise<void> => {
    const setKeys = [
      { key: 'quantityPerSizCol', value: '' },
      { key: 'pricePerUnit', value: '' },
    ];
    const colourwayOptionsArrOpt = iscolourWayToggle ? colourwayOptionsArr || [] : [];
    const sizeOptionsArrOpt = isSizeLengthToggle ? sizeOptionsArr || [] : [];
    const productLengthsOpt = hideLengthSysMaster && isSizeLengthToggle ? productLengths || [] : [];
    const rawData: ComboGroup[] = (await createCostingDynamicGroupedArray(colourwayOptionsArrOpt, sizeOptionsArrOpt, productLengthsOpt, setKeys)) as ComboGroup[];
    const manipullatedAverageData = rawData.map((group) => ({
      ...group,
      expandData: (group.expandData || []).map((item) => ({
        ...item,
        defaultRowIndex: 0,
        multiQuantityRows: [{ quantityPerSizCol: '', pricePerUnit: '', totalPrice: '' }],
      })),
    }));
    setProductCostingState((prevState) => ({
      ...prevState,
      averageCosting: {
        ...prevState.averageCosting,
        averageCostingInfo: manipullatedAverageData as never,
        totSize: sizeOptionsArrOpt.map((data) => data?.name),
        totLength: productLengthsOpt.map((data) => data?.name),
        totColourways: colourwayOptionsArrOpt.map((data) => data?.name),
        overAllSumOfTotalPrice: '0' as never,
        overAllSumOfQuantityPerSizCol: '0' as never,
        overAllSumOfPricePerUnit: '0' as never,
      },
    }));
  };

  const handleDetailedCosting = async (): Promise<void> => {
    const copyState = productCostingstate;
    const data = await manipualteDetailedCostingDataOnChange(copyState, supplierRef?._id);
    await handleQuantities(data as never, undefined as never);
    await handleTesting(data);
    if (productTemplate) {
      const res = (await dispatch(getCostingTemplateDetailsById(productTemplate._id))) as unknown as CostingTemplateLike;
      await handleAdditonalModule(res, data as unknown as AdditionalState, undefined, true);
    }
  };

  const handleQuantities = async (iscolourWayToggle: boolean, isSizeLengthToggle: boolean): Promise<void> => {
    const copyState: DetailedQuantitiesState = cloneDeep(detailedQuanitiesState);
    const setKeys = [{ key: 'quantityPerSizCol', value: '' }];
    const colourwayOptionsArrOpt = iscolourWayToggle ? colourwayOptionsArr || [] : [];
    const sizeOptionsArrOpt = isSizeLengthToggle ? sizeOptionsArr || [] : [];
    const productLengthsOpt = hideLengthSysMaster && isSizeLengthToggle ? productLengths || [] : [];

    const manipultedQuantitesData = (await createCostingDynamicGroupedArray(colourwayOptionsArrOpt, sizeOptionsArrOpt, productLengthsOpt, setKeys)) as ComboGroup[];
    set(copyState, quantitiesFieldpath().QUANTITES_INFO, manipultedQuantitesData);
    set(
      copyState,
      quantitiesFieldpath().TOTAL_AVERAGE_SIZES,
      sizeOptionsArrOpt?.map((data) => data.name),
    );
    set(
      copyState,
      quantitiesFieldpath().TOTAL_AVERAGE_LENGTH,
      productLengthsOpt?.map((data) => data.name),
    );
    set(
      copyState,
      quantitiesFieldpath().TOTAL_AVERAGE_COLOURWAYS,
      colourwayOptionsArrOpt?.map((data) => data.name),
    );
    setDetailedQuanitiesState((prevState) => ({ ...prevState, ...copyState }));
  };

  const handleTesting = async (copyState: ProductCostingState): Promise<void> => {
    const testing: TestingRowLike[] = [];
    (TestingLibraries || []).forEach((test) => {
      const obj: TestingRowLike = {
        colourway: null,
        colourRef: test?.colour as NamedRefLike | string | null,
        testingCode: test as NamedRefLike,
        testingDesc: test.description,
        testingType: test.type,
        totalPrice: '',
        isExcluded: false,
        isChangedBySupplierTotalPrice: false,
        isChangedBySupplierPricePerUnit: false,
        pricePerUnit: '',
        bomComponent: test?.bomComponent?.referenceCode,
      };
      testing.push(obj);
    });
    set(copyState, testingFieldpath().TESTING_INFO_DATA, testing);
    setProductCostingState((prevState) => ({ ...prevState, ...copyState }));
  };

  /**
   * Recomputes the landed chain for an array of rows.
   *
   * `supplierOverride` carries `{ agentFee }` for callers running before a newly selected supplier
   * has been committed to state. Omitted by every other caller, which keeps reading the agent fee
   * from state.
   */
  const calculateProfit = async (
    array: LandedRow[] | undefined,
    costingTemplateDetails: CostingTemplateLike,
    incotermVal?: NamedRefLike | null,
    exchangeOverride?: ExchangeOverride,
    supplierOverride?: SupplierOverride,
  ): Promise<LandedRow[] | undefined> => {
    const incotermState = incotermVal || incoterms;
    const agentFeePercent = supplierOverride ? supplierOverride?.agentFee : supplierRef?.companies?.agentFee;
    const cloneTestingState = cloneDeep(testingInfo);
    const setProductCostingStateData = cloneData(additionalInfo);
    const sumPricePerUnitTestingSectionData = sumPricePerUnitTestingAdditionalSection(cloneTestingState, colourWayToggle, 'pricePerUnit');
    const sumPricePerUnitSetupCostData = sumPricePerUnitTestingAdditionalSection(setProductCostingStateData, colourWayToggle, 'totalPrice');

    if (Array.isArray(array) && array.length > 0) {
      const newArray: LandedRow[] = [];
      const freightAutomatedActive = isFreightAutomatedActive(costingTemplateDetails?.defaultColumns);
      const freightConversion = freightConversionRate({
        exchangeRateList: productExchnageRate?.exchangeRateList,
        dcCurrency: exchangeOverride?.dcCurrency || userInfo?.companyCurrency,
        freightCurrency: automatedFreight?.currency,
        supplierCurrency: productCostingstate?.productInfo?.currency,
        dcExchangeRate: exchangeOverride?.primaryCurrencyValue || internalState?.primaryCurrValue,
        dcCompanyCurrencyValue: exchangeOverride?.secondaryCurrencyValue,
      });
      const costingQuantity = totalCostingQuantity(array);
      const freightAutomatedPerUnit = freightAutomatedActive ? automatedFreightPerUnit(automatedFreight, costingQuantity, freightConversion) : null;
      const freightAutomatedTotalCost = freightAutomatedActive ? automatedFreightTotal(automatedFreight, freightConversion) : null;
      const freightAutomatedIssue = freightAutomatedActive ? freightAutomatedRowIssue(automatedFreight, freightConversion, costingQuantity) : '';
      for (const item of array) {
        let data: LandedRow = cloneDeep(item);
        if (!freightAutomatedActive) {
          data.freightAutomated = '0';
          data.freightAutomatedTotal = '0';
        } else if (automatedFreight) {
          data.freightAutomated = String(freightAutomatedPerUnit || 0);
          data.freightAutomatedTotal = String(freightAutomatedTotalCost || 0);
          data.freightAutomatedIssue = freightAutomatedIssue;
        }
        const landedCost = checkIsNanAndNumber(data?.pricePerUnitIncoConverted);
        let landedInfoPercent = 0;
        let landedInfoAmount = 0;
        if ('landedInfo' in data && Array.isArray(data.landedInfo) && data.landedInfo.length > 0) {
          data.landedInfo = data?.landedInfo?.map((info) => {
            if (info.type === 'amount') landedInfoAmount = checkIsNanAndNumber(landedInfoAmount) + checkIsNanAndNumber(info.tempValue);
            else if (info.type === 'percentage') {
              const calculatedValue = landedCost * (checkIsNanAndNumber(info?.tempValue) / 100);
              landedInfoPercent = checkIsNanAndNumber(landedInfoPercent) + checkIsNanAndNumber(calculatedValue);
              info.calculatedValue = calculatedValue;
            }
            return info;
          });
        }
        let supplierFee = checkIsNanAndNumber(agentFeePercent || 0);
        supplierFee = landedCost * (supplierFee / 100);
        data = await calculateLandedDefaultColumnsValues(
          data,
          supplierFee,
          costingTemplateDetails,
          sumPricePerUnitTestingSectionData,
          sumPricePerUnitSetupCostData,
          exchangeOverride,
          supplierOverride,
        );
        data = await calculateBottomColumnValues(
          data,
          costingTemplateDetails,
          {
            landedInfoAmount,
            landedInfoPercent,
            totalDefaultInfoAmount: data?.totalDefaultInfoAmount,
            totalDefaultInfoPercent: data?.totalDefaultInfoPercent,
          },
          (incotermVal || incotermState) as NamedRefLike,
          sumPricePerUnitTestingSectionData,
          sumPricePerUnitSetupCostData,
          exchangeOverride,
        );
        data.pricePerUnitLanded = roundOff(data?.pricePerUnitLanded, 2) || 0;
        data.totalLanded = roundOff(Number(data?.pricePerUnitLanded) * Number(data?.quantityPerSizCol), 2) || 0;
        newArray.push(data);
      }
      return newArray;
    }
    return [];
  };

  const grandTotalLandedInfoCalculation = async (
    grandTotalRows: ComboGroup[] | undefined,
    landedInfoArr: LandedRow[] | undefined,
    exchangedRate?: unknown,
    companyCurrencyValue?: unknown,
  ): Promise<LandedRow[]> => {
    const cloneLandedInfo: LandedRow[] = cloneDeep(landedInfoArr || []);
    const grandCombinationData: ComboRow[] = flatMap(grandTotalRows?.map((val) => val?.expandData));
    const newLandedArray: LandedRow[] = [];
    if (isArray(grandCombinationData) && grandCombinationData.length > 0) {
      for (const val of cloneLandedInfo) {
        let sizess: string | null = null;
        let colours: string | null = null;
        let lenghtss: string | null = null;
        if (colourWayToggle && sizeLengthToggle) {
          colours = val.colourway ? get(val, 'colourway._id', null) : get(val, 'colourWay._id', null);
          sizess = get(val, 'size', '');
          if ((productLengths?.length ?? 0) > 0) {
            lenghtss = get(val, 'length._id', null);
          }
        } else if (colourWayToggle) {
          colours = val.colourway ? get(val, 'colourway._id', null) : get(val, 'colourWay._id', null);
          sizess = null;
          lenghtss = null;
        } else if (sizeLengthToggle) {
          colours = null;
          sizess = get(val, 'size', '');
          lenghtss = null;
          if ((productLengths?.length ?? 0) > 0) {
            lenghtss = get(val, 'length._id', null);
          }
        } else {
          colours = null;
          sizess = null;
          lenghtss = null;
        }
        const customSectionColourSize = {
          colourWayId: isEmpty(colours) ? null : colours?.toString(),
          size: !sizess?.trim() ? '' : sizess,
          lengthId: isEmpty(lenghtss) ? null : lenghtss?.toString(),
        };
        const getData = grandCombinationData?.find((entry) => {
          const entryColours: string | null = entry.colourway ? get(entry, 'colourway._id', null) : get(entry, 'colourWay._id', null);
          const entrySize: string = get(entry, 'size', '');
          const entryLength: string | null = get(entry, 'length._id', null);
          const obj = {
            colourWayId: isEmpty(entryColours) ? null : entryColours?.toString(),
            size: !entrySize?.trim() ? '' : entrySize,
            lengthId: isEmpty(entryLength) ? null : entryLength?.toString(),
          };

          return isEqual(customSectionColourSize, obj);
        });
        if (getData) {
          const pricePerUnitIncoterm = checkIsNanAndNumber(getData?.pricePerUnit);
          const secondaryCurrency = productExchnageRate?.defaultCurrencyValue ? productExchnageRate?.defaultCurrencyValue : 0;
          const secondaryCurrencyValue = companyCurrencyValue || secondaryCurrency;
          const primaryCurrencyValue = exchangedRate || internalState?.primaryCurrValue;
          const pricePerUnitIncoConverted = convertedCurrValue(pricePerUnitIncoterm, primaryCurrencyValue, secondaryCurrencyValue);

          const totalIncoterm = checkIsNanAndNumber(getData?.totalPrice);
          const totalIncoConverted = convertedCurrValue(totalIncoterm, primaryCurrencyValue, secondaryCurrencyValue);
          val.pricePerUnitInco = roundOff(pricePerUnitIncoterm, 2);
          val.totalInco = roundOff(totalIncoterm, 2);
          val.quantityPerSizCol = getData?.quantityPerSizCol;
          val.pricePerUnitIncoConverted = roundOff(pricePerUnitIncoConverted, 2);
          val.totalIncoConverted = roundOff(totalIncoConverted, 2);
        }
        newLandedArray.push(val);
      }
      return newLandedArray;
    }
    return cloneLandedInfo;
  };

  const setBreakDownLandedInfo = async (currlandedInfo: LandedRow[] | undefined): Promise<LandedRow[] | undefined> => {
    if (!isBreakdown) return currlandedInfo;
    if (isBreakdown) {
      let sizeRange: unknown[] = [];
      let lengthRange: NamedRefLike[] = [];
      if (Array.isArray(currlandedInfo) && currlandedInfo.length > 0) {
        for (const land of currlandedInfo) {
          if (land?.size) {
            sizeRange.push(land?.size);
          }
          if (land?.length) {
            lengthRange.push(land?.length);
          }
        }
        return chain(currlandedInfo)
          .groupBy((item: LandedRow) => {
            if (item?.colourway && (item?.colourway as NamedRefLike)?._id) {
              return `${(item?.colourway as NamedRefLike)?._id}`;
            }
            if (item?.colourWay && (item?.colourWay as NamedRefLike)?._id) {
              return `${(item?.colourWay as NamedRefLike)?._id}`;
            }
            return undefined as unknown as string;
          })
          .map(function (items: LandedRow[]) {
            const obj: LandedRow = { ...(items[0] as LandedRow) };
            if (Array.isArray(sizeRange) && sizeRange.length > 0) {
              sizeRange = sizeRange?.filter((v, i, a) => a.findIndex((v2) => v2 === v) === i);
              obj.sizeRange = sizeRange;
            }
            if (Array.isArray(lengthRange) && lengthRange.length > 0) {
              lengthRange = lengthRange?.filter((v, i, a) => a.findIndex((v2) => v2?.name === v?.name) === i);
              obj.lengthRange = lengthRange;
            }
            return obj;
          })
          .value();
      }
      return [];
    }
    return undefined;
  };

  /**
   * Calculates the landed default-column values for a single landed row.
   *
   * A supplier without an agent fee must clear the column, so the presence of `supplierOverride` —
   * not the value inside it — decides whether the supplier held in state is still the source.
   */
  const calculateLandedDefaultColumnsValues = async (
    data: LandedRow,
    supplierFee: number,
    costingTemplateDetails: CostingTemplateLike,
    sumPricePerUnitTestingSectionData: Record<string, unknown> | number,
    sumPricePerUnitSetupCostData: Record<string, unknown> | number,
    exchangeOverride?: ExchangeOverride,
    supplierOverride?: SupplierOverride,
  ): Promise<LandedRow> => {
    const agentFeePercent = supplierOverride ? supplierOverride?.agentFee : supplierRef?.companies?.agentFee;
    const landedCost = checkIsNanAndNumber(data?.pricePerUnitIncoConverted);
    const primaryCurrencyValue = Number(exchangeOverride?.primaryCurrencyValue || internalState?.primaryCurrValue || 1);
    let defaultInfoPercent = 0;
    let defaultInfoAmount = 0;
    if ('defaultColumns' in costingTemplateDetails && Array.isArray(costingTemplateDetails.defaultColumns) && costingTemplateDetails.defaultColumns.length > 0) {
      for (const info of costingTemplateDetails?.defaultColumns || []) {
        const aliasName = info.aliasName as string;
        if (isSwitchedOffFreightColumn(info)) {
          if (info.type === 'percentage') data[`${aliasName}CalculatedValue`] = '0';
          continue;
        }
        if (info.type === 'amount') {
          defaultInfoAmount = checkIsNanAndNumber(defaultInfoAmount) + checkIsNanAndNumber(data[aliasName]);
        } else if (info.type === 'percentage') {
          if (['dutyPercentage', 'tariffCostPercentage']?.includes(aliasName)) {
            let colorWiseTesting = 0;
            let colourWiseSetupCost = 0;
            const rowColourId = (data?.colourway as NamedRefLike)?._id || (data?.colourWay as NamedRefLike)?._id;
            if (rowColourId) {
              colorWiseTesting = Number((sumPricePerUnitTestingSectionData as Record<string, unknown>)[rowColourId] || 0);
              colourWiseSetupCost = Number((sumPricePerUnitSetupCostData as Record<string, unknown>)[rowColourId] || 0);
            } else {
              colorWiseTesting = Number(sumPricePerUnitTestingSectionData || 0);
              colourWiseSetupCost = Number(sumPricePerUnitSetupCostData || 0);
            }
            const setupTestingDeduction =
              aliasName === 'dutyPercentage' ? (checkIsNanAndNumber(colorWiseTesting) + checkIsNanAndNumber(colourWiseSetupCost)) / primaryCurrencyValue : 0;

            const previousCalculatedValue = checkIsNanAndNumber(data[`${aliasName}CalculatedValue`]) || 0;

            if (checkIsNanAndNumber(data[aliasName]) > 0) {
              const freightContribution = resolveFreightContribution(data, costingTemplateDetails?.defaultColumns || [], landedCost);
              const dutyBase = landedCost + freightContribution - setupTestingDeduction;
              const calculatedValue = dutyBase * (checkIsNanAndNumber(data[aliasName]) / 100);
              defaultInfoPercent = checkIsNanAndNumber(defaultInfoPercent) + checkIsNanAndNumber(calculatedValue);
              data[`${aliasName}CalculatedValue`] = calculatedValue;
            } else {
              defaultInfoPercent = checkIsNanAndNumber(defaultInfoPercent) - previousCalculatedValue;
              data[`${aliasName}CalculatedValue`] = '0';
            }
          } else if (['agentsFee'].includes(aliasName)) {
            data[aliasName] = agentFeePercent;
            data[`${aliasName}CalculatedValue`] = supplierFee || '0';

            defaultInfoPercent = checkIsNanAndNumber(defaultInfoPercent) + checkIsNanAndNumber(supplierFee);
          } else if (['freightPercentage'].includes(aliasName)) {
            const freightPercentageValue = landedCost * (checkIsNanAndNumber(data[aliasName]) / 100) || '0';
            data[`${aliasName}CalculatedValue`] = checkIsNanAndNumber(freightPercentageValue);
            defaultInfoPercent = checkIsNanAndNumber(defaultInfoPercent) + checkIsNanAndNumber(freightPercentageValue);
          } else {
            data[`${aliasName}CalculatedValue`] = '0';
          }
        }
      }
    }
    data.totalDefaultInfoAmount = defaultInfoAmount || 0;
    data.totalDefaultInfoPercent = defaultInfoPercent || 0;
    return data;
  };

  /** Resolves the sub-brand (retail) currency for the current product from the sub-brand master. */
  const getSubBrandCurrency = (): string | undefined => {
    const subBrandId = (productDetails?.subBrandId as NamedRefLike)?._id || (productDetails?.subBrandId as string | undefined);
    return subBrandList?.find((sb) => sb?._id === subBrandId)?.currency;
  };

  /**
   * Resolves the retail exchange rate (sub-brand currency → distribution-centre currency) from the
   * season's exchange-rate master: matches the exchangeRateList entry whose defaultCurrency is the
   * sub-brand currency, then the combinationCurrencies entry whose currency is the
   * distribution-centre currency. Returns 1 when the list, currencies, match, or value is missing.
   */
  const getRetailExRate = (dcCurrency?: string): number => {
    /* `Array.isArray` narrows where lodash's `isArray` does not, so the list stays non-nullable
       below without changing the guard's behaviour. */
    const exchangeRateList = productExchnageRate?.exchangeRateList;
    if (!Array.isArray(exchangeRateList) || !exchangeRateList.length) return 1;
    const subBrandCurrency = getSubBrandCurrency();
    if (!subBrandCurrency || !dcCurrency) return 1;
    const matched = exchangeRateList.find((val) => val?.defaultCurrency === subBrandCurrency);
    const found = matched?.combinationCurrencies?.find((comb) => comb?.currency === dcCurrency);
    return checkIsNanAndNumber(found?.value) || 1;
  };

  const calculateBottomColumnValues = async (
    data: LandedRow,
    costingTemplateDetails: CostingTemplateLike,
    {
      landedInfoAmount,
      landedInfoPercent,
      totalDefaultInfoAmount,
      totalDefaultInfoPercent,
    }: { landedInfoAmount: unknown; landedInfoPercent: unknown; totalDefaultInfoAmount: unknown; totalDefaultInfoPercent: unknown },
    incotermVal?: NamedRefLike | null,
    _sumPricePerUnitTestingSectionData?: Record<string, unknown> | number,
    _sumPricePerUnitSetupCostData?: Record<string, unknown> | number,
    exchangeOverride?: ExchangeOverride,
  ): Promise<LandedRow> => {
    const hasRetailCurrency = Boolean(getSubBrandCurrency());

    if ((costingTemplateDetails?.bottomColumnArr?.length ?? 0) > 0) {
      for (const bottom of costingTemplateDetails.bottomColumnArr as TemplateColumn[]) {
        const aliasName = bottom.aliasName as string;
        if (aliasName === 'pricePerUnitLanded') {
          data[aliasName] =
            checkIsNanAndNumber(data?.pricePerUnitIncoConverted) +
            checkIsNanAndNumber(landedInfoAmount) +
            checkIsNanAndNumber(landedInfoPercent) +
            checkIsNanAndNumber(totalDefaultInfoAmount) +
            checkIsNanAndNumber(totalDefaultInfoPercent);
        } else if (aliasName === 'totalLanded') {
          data[aliasName] = checkIsNanAndNumber(data?.pricePerUnitLanded) * checkIsNanAndNumber(data?.quantityPerSizCol);
        } else if (aliasName === 'vat') {
          data[`${aliasName}CalculatedValue`] =
            checkIsNanAndNumber(data?.sellingPriceGross) - checkIsNanAndNumber(data?.sellingPriceGross) / (1 + checkIsNanAndNumber(data.vat) / 100);
        } else if (aliasName === 'customerDiscount') {
          data[`${aliasName}CalculatedValue`] = checkIsNanAndNumber(data?.sellingPriceGross) * (checkIsNanAndNumber(data.customerDiscount) / 100);
        } else if (aliasName === 'sellingPriceNet') {
          data[aliasName] =
            checkIsNanAndNumber(data?.sellingPriceGross) - checkIsNanAndNumber(data?.vatCalculatedValue) - checkIsNanAndNumber(data?.customerDiscountCalculatedValue);
          if (
            'defaultColumns' in costingTemplateDetails &&
            Array.isArray(costingTemplateDetails.defaultColumns) &&
            costingTemplateDetails.defaultColumns.length > 0 &&
            (productDetails?.licenseId || productDetails?.likenessId)
          ) {
            costingTemplateDetails?.defaultColumns
              ?.filter((vl) => ['licenseRoyaltyFee', 'likenessRoyaltyFee']?.includes(vl?.aliasName as string))
              ?.forEach((info) => {
                const infoAlias = info.aliasName as string;
                if (['licenseRoyaltyFee'].includes(infoAlias) && productDetails?.licenseId) {
                  if (incotermVal && (licenseList?.length ?? 0) > 0) {
                    const getLicenceData = licenseList?.find((item) => item?._id === productDetails?.licenseId);

                    if (getLicenceData) {
                      const getIncotermsValue = getLicenceData?.incoterms?.find((item) => item?.incotermsId?._id === incotermVal?._id);
                      if (getIncotermsValue) {
                        data[infoAlias] = getIncotermsValue?.value;
                        const royaltyFee = (checkIsNanAndNumber(getIncotermsValue?.value) / 100) * Number(data?.sellingPriceNet);
                        data[`${infoAlias}CalculatedValue`] = royaltyFee;
                        data.pricePerUnitLanded = checkIsNanAndNumber(data?.pricePerUnitLanded) + checkIsNanAndNumber(royaltyFee);
                      }
                    }
                  }
                } else if (['likenessRoyaltyFee'].includes(infoAlias) && productDetails?.likenessId) {
                  const getLikenessData = likenessList?.find((item) => item?._id === productDetails?.likenessId);
                  if (getLikenessData) {
                    const getIncotermsValue = getLikenessData?.incoterms?.find((item) => item?.incotermsId?._id === incotermVal?._id);
                    if (getIncotermsValue) {
                      data[infoAlias] = getIncotermsValue?.value;
                      const royaltyFee = (checkIsNanAndNumber(getIncotermsValue?.value) / 100) * Number(data?.sellingPriceNet);
                      data[`${infoAlias}CalculatedValue`] = royaltyFee;
                      data.pricePerUnitLanded = checkIsNanAndNumber(data?.pricePerUnitLanded) + checkIsNanAndNumber(royaltyFee);
                    }
                  }
                }
              });
          }
          data.totalLanded = checkIsNanAndNumber(data?.pricePerUnitLanded) * checkIsNanAndNumber(data?.quantityPerSizCol);
        } else if (aliasName === 'grossProfitPerUnit') {
          data[aliasName] = checkIsNanAndNumber(data?.sellingPriceNet) - checkIsNanAndNumber(data?.pricePerUnitLanded);
        } else if (aliasName === 'markupPerUnit') {
          const sellingPriceNet = checkIsNanAndNumber(data?.sellingPriceNet);
          const pricePerUnitLanded = checkIsNanAndNumber(data?.pricePerUnitLanded);
          data[aliasName] = pricePerUnitLanded === 0 ? 0 : sellingPriceNet / pricePerUnitLanded;
        } else if (aliasName === 'marginPerUnit') {
          const sellingPriceNet = checkIsNanAndNumber(data?.sellingPriceNet);
          const pricePerUnitLanded = checkIsNanAndNumber(data?.pricePerUnitLanded);

          if (sellingPriceNet === 0) {
            data[`${aliasName}`] = 0;
          } else {
            data[`${aliasName}`] = ((sellingPriceNet - pricePerUnitLanded) / sellingPriceNet) * 100;
          }
        } else if (aliasName === 'estimatedProfitPerUnit') {
          if (!hasRetailCurrency) {
            data[aliasName] = '--';
          } else {
            const retailExRate = getRetailExRate(exchangeOverride?.dcCurrency);
            data[aliasName] = checkIsNanAndNumber(data?.estimatedRetailValue) - checkIsNanAndNumber(data?.sellingPriceNet) * retailExRate;
          }
        } else if (aliasName === 'retailMarginPerUnit') {
          if (!hasRetailCurrency) {
            data[aliasName] = '--';
          } else {
            const estimatedRetailValue = checkIsNanAndNumber(data?.estimatedRetailValue);
            const estimatedProfitPerUnit = checkIsNanAndNumber(data?.estimatedProfitPerUnit);
            if (estimatedRetailValue === 0) {
              data[`${aliasName}`] = 0;
            } else {
              data[`${aliasName}`] = (estimatedProfitPerUnit / estimatedRetailValue) * 100;
            }
          }
        }
      }
    }
    return data;
  };

  /**
   * Sums testing total prices into sumOfPrice and commits the testing state.
   * Re-clones testingInfo into a fresh array reference before committing so the consumer's useMemo
   * (keyed on the array identity) recomputes — mutating the existing array in place leaves the
   * rendered rowData one edit behind.
   */
  const calculateTotPrice = async (copyState: TestingState, key: string): Promise<void> => {
    const testData = copyState?.testing?.testingInfo?.filter((val) => !val.isExcluded);
    const data = await calculateTotValues(testData, [{ key: 'totalPrice', updateKey: 'sumOfPrice' }]);
    set(copyState, testingFieldpath().SUM_PRICE, data[key]);
    set(copyState, testingFieldpath().TESTING_INFO_DATA, cloneData(copyState?.testing?.testingInfo || []));
    setTestingState((state) => ({ ...state, ...copyState }));
  };

  const calculatePricePerUnit = async (copyState: TestingState, value?: unknown, index?: number, pricePerPath?: string): Promise<void> => {
    const quantitiesArray = averageCostingActive
      ? (get(productCostingstate, 'averageCosting.averageCostingInfo') as ComboGroup[] | undefined)
      : (get(detailedQuanitiesState, 'quantities.quantitiesInfo') as ComboGroup[] | undefined);
    const quantitiesQuantityTotal = averageCostingActive
      ? get(productCostingstate, 'averageCosting.overAllSumOfQuantityPerSizCol')
      : get(detailedQuanitiesState, 'quantities.overAllSumOfQuantityPerSizCol');
    const testingRows = get(copyState, 'testing.testingInfo', []) as TestingRowLike[];

    const computePrice = (item: TestingRowLike | undefined, inputValue: unknown): unknown => {
      const colourId = item?.colourway?._id;
      if (colourId && Array.isArray(quantitiesArray)) {
        const findColourData = quantitiesArray?.find((vl) => {
          if (vl?.colourway?._id === colourId) {
            return vl;
          }
          return undefined;
        });

        return Number(findColourData?.sumOfQuantityPerSizCol) > 0 ? roundOff(Number(inputValue) / Number(findColourData?.sumOfQuantityPerSizCol), 2) : 0;
      }
      return Number(quantitiesQuantityTotal) > 0 ? roundOff(Number(inputValue) / Number(quantitiesQuantityTotal), 2) : 0;
    };

    if (typeof index === 'number') {
      const price = computePrice(testingRows[index], value);
      set(copyState, pricePerPath as string, price || '0');
    } else {
      for (let i = 0; i < testingRows.length; i++) {
        const itemPricePath = `testing.testingInfo[${i}].pricePerUnit`;
        const colourWayId = testingRows?.[i]?.colourway?._id;
        if (colourWayToggle && !colourWayId) {
          set(copyState, itemPricePath, '0');
        } else {
          const price = computePrice(testingRows[i], testingRows[i]?.totalPrice);
          set(copyState, itemPricePath, price || '0');
        }
      }
    }
    set(copyState, testingFieldpath().TESTING_INFO_DATA, cloneData(copyState?.testing?.testingInfo || []));
    setTestingState((state) => ({ ...state, ...copyState }));
  };

  const calculateTotalPrice = async (copyState: TestingState, value?: unknown, index?: number, testPricePath?: string): Promise<void> => {
    const quantitiesArray = averageCostingActive
      ? (get(productCostingstate, 'averageCosting.averageCostingInfo') as ComboGroup[] | undefined)
      : (get(detailedQuanitiesState, 'quantities.quantitiesInfo') as ComboGroup[] | undefined);

    const overallQuantity = averageCostingActive
      ? get(productCostingstate, 'averageCosting.overAllSumOfQuantityPerSizCol')
      : get(detailedQuanitiesState, 'quantities.overAllSumOfQuantityPerSizCol');

    const testingRows = get(copyState, 'testing.testingInfo', []) as TestingRowLike[];

    const computeTotalPrice = (item: TestingRowLike | undefined, inputValue: unknown): number => {
      const unitPrice = inputValue ?? item?.pricePerUnit ?? 0;
      const colourId = item?.colourway?._id;

      if (colourId && Array.isArray(quantitiesArray)) {
        const match = quantitiesArray.find((q) => q?.colourway?._id === colourId);
        const sum = match?.sumOfQuantityPerSizCol || 0;
        return Number(unitPrice) * Number(sum);
      }

      return Number(unitPrice) * Number(overallQuantity);
    };

    if (typeof index === 'number') {
      const item = testingRows[index];
      const totalPrice = computeTotalPrice(item, value);
      set(copyState, testPricePath as string, totalPrice || 0);
    } else {
      for (let i = 0; i < testingRows.length; i++) {
        const item = testingRows[i];
        const path = `testing.testingInfo[${i}].totalPrice`;
        const totalPrice = computeTotalPrice(item, value);
        set(copyState, path, totalPrice || 0);
      }
    }

    const data = cloneData(copyState.testing.testingInfo);
    set(copyState, testingFieldpath().TESTING_INFO_DATA, data);
    setTestingState((state) => ({ ...state, ...copyState }));
  };

  const handleInitialTesting = async (): Promise<void> => {
    const copyState: TestingState = cloneDeep(testingState);
    const testDataPath = testingFieldpath().TESTING_INFO_DATA;
    const testingData = get(copyState, testDataPath, []) as TestingRowLike[];
    const updatedTestingData = testingData?.map((item) => ({
      ...item,
      colourway: null,
      pricePerUnit: '',
    }));
    set(copyState, testDataPath, updatedTestingData);
    await calculateTotPrice(copyState, 'sumOfPrice');
  };

  const renderDetailedCostingArr = async (
    selectedBom: BomRow | BomRow[],
    isAllBom: boolean,
    colourwayToggle: boolean,
    isSizeLengthToggle: boolean,
    _suppid?: string,
  ): Promise<typeof detailedCostingState | undefined> => {
    const copyState = cloneDeep(detailedCostingState);
    set(copyState, detailedCostingFieldpath().DETAILED_COSTING_ACTIVE, true);
    let existingBom = copyState?.detailedCosting?.bomTypeArr as unknown as BomRow[];
    const existingBomExpandDataMap: Record<string, ComboRow[]> = {};
    ((copyState?.detailedCosting?.bomTypeArr || []) as unknown as BomRow[]).forEach((bom) => {
      if (bom?._id && bom?.expandData?.length) {
        existingBomExpandDataMap[bom._id] = bom.expandData;
      }
    });
    if (!isAllBom) {
      const checkAlreadyExsit = existingBom?.find((vl) => vl?._id === (selectedBom as BomRow)?._id);
      if (checkAlreadyExsit) {
        toasterMsg('BOM already exist', 'danger');
        return undefined;
      }
      existingBom.push(selectedBom as BomRow);
    } else {
      existingBom = selectedBom as BomRow[];
    }
    const updatedArray: BomRow[] = [];
    const colourwayOptionsArrOpt = colourwayToggle ? colourwayOptionsArr || [] : [];
    const sizeOptionsArrOpt = isSizeLengthToggle ? sizeOptionsArr || [] : [];
    const productLengthsOpt = isSizeLengthToggle && hideLengthSysMaster ? productLengths || [] : [];
    const setKeys = [
      { key: 'quantityPerSizCol', value: '' },
      { key: 'quantityUnitOfMeasure', value: '0' },
      { key: 'pricePerUnit', value: '0' },
      {
        key: 'isExcluded',
        value: false,
      },
    ];
    const manipullatedAverageData: ComboGroup[] = (await createCostingDynamicGroupedArray(colourwayOptionsArrOpt, sizeOptionsArrOpt, productLengthsOpt, setKeys)) as ComboGroup[];
    for (const element of existingBom) {
      element.overAllQuantity = '0';
      element.overAllTotalPrice = '0';
      element.isExpand = false;
      element.totLength = [...new Set(productLengthsOpt.map((data) => data?.name))];
      element.totSize = [...new Set(sizeOptionsArrOpt.map((data) => data?.name))];
      element.totColourways = [...new Set(colourwayOptionsArrOpt.map((data) => data?.name))];
      element.totColourRefs = [...new Set(colourwayOptionsArrOpt.map((data) => data?.newReferences))];
      element.expandData = flatMap(manipullatedAverageData?.map((val) => val?.expandData));
      element.expandData = (element.expandData ?? []).map((vl): ComboRow => {
        return {
          ...vl,
          pricePerUnit: element?.pricePerUnit || '0',
          quantityUnitOfMeasure: (element?.quantityUnitOfMeasure as NamedRefLike | null) || null,
          quantityPerSizCol: element?.quantity,
        };
      });
      const savedExpandData = existingBomExpandDataMap[element?._id as string];
      if (savedExpandData?.length) {
        const makeKey = (entry: ComboRow): string =>
          `${(entry?.colourway as NamedRefLike)?._id || entry?.colourway || ''}_${(entry?.size as unknown as NamedRefLike)?._id || entry?.size || ''}_${(entry?.length as NamedRefLike)?._id || entry?.length || ''}`;
        const savedMap: Record<string, ComboRow> = {};
        savedExpandData.forEach((entry) => {
          savedMap[makeKey(entry)] = entry;
        });
        element.expandData = (element.expandData ?? []).map((newEntry): ComboRow => {
          const saved = savedMap[makeKey(newEntry)];
          if (!saved) return newEntry;
          return {
            ...newEntry,
            pricePerUnit: saved.pricePerUnit,
            quantityUnitOfMeasure: saved.quantityUnitOfMeasure,
            quantityPerSizCol: saved.quantityPerSizCol,
            isExcluded: saved.isExcluded,
          };
        });
      }
      updatedArray.push(element);
    }
    set(copyState, 'detailedCosting.bomTypeArr', updatedArray);
    await calculateDetailCosting(copyState, undefined, false, productCostingstate?.productInfo?.currency);
    return copyState;
  };

  /**
   * Rebuilds the Additional Component module from the costing template.
   * Preserves the user-entered values for any template row whose item name (title) already exists
   * in the current state, so switching costing type does not wipe data the template still references.
   */
  const handleAdditonalComponentModule = async (
    res: CostingTemplateLike,
    copyState: AdditionalComponentState,
    controlSetState?: boolean,
  ): Promise<AdditionalComponentState | undefined> => {
    const normaliseTitle = (title: unknown): unknown => (typeof title === 'string' ? title.trim().toLowerCase() : title);
    const existingRows = (get(copyState, additionalComponentFieldPath().SELECTED_TEMP_ADDTIONAL_DATA) || []) as ComboRow[];
    const existingByTitle = new Map<unknown, ComboRow[]>();
    existingRows.forEach((row) => {
      const key = normaliseTitle(row?.title);
      if (key === undefined || key === null || key === '') return;
      if (!existingByTitle.has(key)) existingByTitle.set(key, []);
      existingByTitle.get(key)?.push(row);
    });
    const takeExistingRow = (title: unknown): ComboRow | null => {
      const bucket = existingByTitle.get(normaliseTitle(title));
      return bucket && bucket.length ? (bucket.shift() as ComboRow) : null;
    };
    let hasPreservedRow = false;

    if (Array.isArray(res?.additionalComponentInfo) && res?.additionalComponentInfo.length > 0) {
      let response = res?.additionalComponentInfo;
      response = response?.map((data) => {
        const preserved = takeExistingRow(data?.title);
        if (preserved) {
          hasPreservedRow = true;
          return {
            ...data,
            colourway: preserved.colourway ?? null,
            quantityUom: preserved.quantityUom ?? null,
            totalQuantityUom: preserved.totalQuantityUom ?? null,
            quantityPerUnit: preserved.quantityPerUnit ?? '',
            pricePerUnit: preserved.pricePerUnit ?? '',
            totalQuantity: preserved.totalQuantity ?? '',
            pricePerUoM: preserved.pricePerUoM ?? '',
            quantityPerColSizLen: preserved.quantityPerColSizLen ?? '',
            totalPrice: preserved.totalPrice ?? '',
            isExcluded: preserved.isExcluded ?? false,
            isPricePerUnitDisabled: preserved.isPricePerUnitDisabled ?? false,
            isTotalPriceDisabled: preserved.isTotalPriceDisabled ?? false,
          };
        }
        return {
          ...data,
          colourway: null,
          quantityUom: null,
          totalQuantityUom: null,
          quantityPerUnit: '',
          pricePerUnit: '',
          totalQuantity: '',
          pricePerUoM: '',
          quantityPerColSizLen: '',
          totalPrice: '',
          isPricePerUnitDisabled: false,
          isTotalPriceDisabled: false,
        };
      });
      set(copyState, additionalComponentFieldPath().SELECTED_TEMP_ADDTIONAL_DATA, response);
      if (hasPreservedRow) {
        const nonExcluded = response.filter((item) => !item.isExcluded);
        const totals = await calculateTotValues(nonExcluded, [{ key: 'totalPrice', updateKey: 'sumOfTotPrice' }]);
        set(copyState, additionalComponentFieldPath().SUM_TOTAL_PRICE, totals.sumOfTotPrice);
        if (controlSetState) {
          return copyState;
        }
        setAdditionalComponentState((prevState) => ({ ...prevState, ...copyState }));
        return undefined;
      }
    } else {
      set(copyState, 'additionalComponent', {
        additionalComponentInfo: [
          {
            colourway: null,
            quantityUom: null,
            totalQuantityUom: null,
            quantityPerUnit: '',
            pricePerUnit: '',
            title: '',
            totalQuantity: '',
            pricePerUoM: '',
            quantityPerColSizLen: '',
            totalPrice: '',
            isPricePerUnitDisabled: false,
            isTotalPriceDisabled: false,
          },
        ],
      });
    }
    set(copyState, additionalComponentFieldPath().SUM_TOTAL_PRICE, '0');
    if (controlSetState) {
      return copyState;
    }
    setAdditionalComponentState((prevState) => ({ ...prevState, ...copyState }));
    return undefined;
  };

  /**
   * Rebuilds the Spec Requirements module from the costing template (BM-9899).
   *
   * Same contract as its siblings above: the template owns which requirements exist, the costing owns
   * the answers. A specification already typed against a requirement the template still declares is
   * carried over; rows the user added here are left alone. The merge itself is a pure function so it
   * can be tested without the provider.
   * @param res the costing template detail as the template endpoint returns it.
   */
  const handleSpecificationRequirementModule = (res: CostingTemplateLike): void => {
    setSpecificationRequirementState((prevState) => ({
      ...prevState,
      specificationRequirement: {
        ...prevState.specificationRequirement,
        specificationRequirementInfo: mergeTemplateRequirements(
          res as unknown as CostingTemplateDetail,
          prevState.specificationRequirement.specificationRequirementInfo,
        ),
      },
    }));
  };


  /**
   * Re-runs the landed chain once the product's automated freight has loaded, so a template with
   * Freight Automated switched on shows the per-unit freight without the user touching a cell.
   */
  const recalculateLandedInfoForAutomatedFreight = async (): Promise<LandedInfoStateEntry[] | null> => recalculateLandedInfoStates();

  return {
    calculateAvrageCosting,
    calculateQuantities,
    manipualteDetailedCostingDataOnChange,
    manipualtedProductCostingSaveData,
    handleLandedInfoModule,
    getSavedLandedCombinationRows,
    recalculateLandedInfoForIncoterm,
    applySupplierChangeToLandedInfo,
    handleAdditonalModule,
    handleAverageCosting,
    handleDetailedCosting,
    calculateProfit,
    grandTotalLandedInfoCalculation,
    setBreakDownLandedInfo,
    calculateTotPrice,
    calculatePricePerUnit,
    calculateTotalPrice,
    handleInitialTesting,
    handleQuantities,
    renderDetailedCostingArr,
    handleAdditonalComponentModule,
    handleSpecificationRequirementModule,
    recalculateLandedInfoForAutomatedFreight,
  };
}
